import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { getMyBookings } from '@/lib/booking-api';
import { fetchPublicTutor } from '@/lib/tutor-api';
import { fetchMyParentProfile } from '@/lib/parent-api';
import type { BookingResponse } from '@/lib/schemas/booking';
import type { PublicTutorView } from '@/lib/schemas/tutor-profile';
import type { ChildResponse } from '@/lib/schemas/parent-profile';
import { BookingDetailPanel } from './BookingDetailPanel';
import { Calendar, CalendarDayHeader } from '@/components/calendar';
import type { YearAndMonth } from '@/components/calendar';
import { BookClassModal } from '@/components/BookClassModal';
import { BookingSuccessModal } from '@/components/BookingSuccessModal';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_COLORS: Record<string, string> = {
  pending:
    'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900/50 dark:hover:bg-yellow-950/60',
  accepted:
    'bg-green-50 text-green-800 border-green-200 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/50 dark:hover:bg-green-950/60',
  declined:
    'bg-red-50 text-red-800 border-red-200 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50 dark:hover:bg-red-950/60',
  cancelled:
    'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-800',
  completed:
    'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50 dark:hover:bg-blue-950/60',
};

export function BookingsPage() {
  const { accessToken } = useAuth();
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [tutorData, setTutorData] = useState<Record<string, PublicTutorView>>({});
  const [childData, setChildData] = useState<Record<string, ChildResponse>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [yearAndMonth, setYearAndMonth] = useState<YearAndMonth>(() => {
    const now = new Date();
    return [now.getFullYear(), now.getMonth() + 1];
  });
  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState<{
    summaryMessage: string;
    tutorName: string;
    childName: string;
  } | null>(null);

  const handleBookingSuccess = (details: {
    summaryMessage: string;
    tutorName: string;
    childName: string;
  }) => {
    setSuccessDetails(details);
    setSuccessModalOpen(true);
  };

  const childrenList = Object.values(childData);

  // Function to refresh bookings data without page reload
  const refreshBookings = async () => {
    if (!accessToken) return;

    try {
      const [bookingsData, parentProfile] = await Promise.all([
        getMyBookings(accessToken),
        fetchMyParentProfile(accessToken),
      ]);

      setBookings(bookingsData);

      const childMap: Record<string, ChildResponse> = {};
      parentProfile.children.forEach((c: ChildResponse) => {
        childMap[c.id] = c;
      });
      setChildData(childMap);

      const tutorIds = new Set(bookingsData.map((b: BookingResponse) => b.tutorId));
      const tutors: Record<string, PublicTutorView> = {};

      for (const tutorId of tutorIds) {
        try {
          const tutor = await fetchPublicTutor(accessToken, String(tutorId));
          tutors[String(tutorId)] = tutor;
        } catch {
          // Silently ignore tutor fetch errors
        }
      }

      setTutorData(tutors);
    } catch (e: unknown) {
      console.error('Failed to refresh bookings:', e);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);

    Promise.all([getMyBookings(accessToken), fetchMyParentProfile(accessToken)])
      .then(async ([bookingsData, parentProfile]) => {
        if (cancelled) return;
        setBookings(bookingsData);

        const childMap: Record<string, ChildResponse> = {};
        parentProfile.children.forEach((c: ChildResponse) => {
          childMap[c.id] = c;
        });
        setChildData(childMap);

        const tutorIds = new Set(bookingsData.map((b: BookingResponse) => b.tutorId));
        const tutors: Record<string, PublicTutorView> = {};

        for (const tutorId of tutorIds) {
          try {
            const tutor = await fetchPublicTutor(accessToken, String(tutorId));
            if (!cancelled) {
              tutors[String(tutorId)] = tutor;
            }
          } catch {
            // Silently ignore tutor fetch errors
          }
        }

        if (!cancelled) {
          setTutorData(tutors);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Failed to load bookings');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Bookings keyed by local date ("yyyy-MM-dd") so they land in the right day cell.
  const bookingsByDate = useMemo(() => {
    const map: Record<string, BookingResponse[]> = {};
    for (const booking of bookings) {
      const dateString = format(new Date(booking.slotStartUtc), 'yyyy-MM-dd');
      (map[dateString] ??= []).push(booking);
    }
    for (const dayBookings of Object.values(map)) {
      dayBookings.sort(
        (a, b) => new Date(a.slotStartUtc).getTime() - new Date(b.slotStartUtc).getTime(),
      );
    }
    return map;
  }, [bookings]);

  function bookingLabel(booking: BookingResponse): string {
    const title = booking.classTitle || tutorData[booking.tutorId]?.displayName || 'Class';
    return booking.seriesId ? `${title} ↻` : title;
  }

  function handleUpdate(updated: BookingResponse) {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }

  const selectedBooking = selectedBookingId
    ? bookings.find((b) => b.id === selectedBookingId)
    : undefined;
  const selectedTutor = selectedBooking ? tutorData[selectedBooking.tutorId] : undefined;
  const selectedBookingChild = selectedBooking ? childData[selectedBooking.childId] : undefined;

  if (loading) {
    return (
      <div className="flex h-full flex-col space-y-4 animate-pulse">
        {/* Page Header Skeleton */}
        <div className="shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="h-7 w-32 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="h-10 w-full sm:w-36 rounded-xl bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>

        {/* Full-page Month Calendar Skeleton */}
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 flex flex-col">
          {/* Mock Calendar Navigation Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <div className="h-9 w-20 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
            <div className="h-6 w-32 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="flex items-center gap-1.5">
              <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
          </div>

          {/* Days of Week Header Grid */}
          <div className="grid shrink-0 grid-cols-7 border-b border-slate-200 dark:border-slate-800">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, index) => (
              <div
                key={d}
                className={cn(
                  'px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400',
                  [0, 6].includes(index) && 'text-slate-400 dark:text-slate-500',
                )}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day Grid Skeletons */}
          <div className="flex-1 grid grid-cols-7 grid-rows-5 divide-x divide-y divide-slate-100 dark:divide-slate-700/60 border-b border-slate-100 dark:divide-slate-700/60">
            {Array.from({ length: 35 }).map((_, idx) => {
              // Inject mock booking strip in a few cells for realism
              const hasBooking = idx === 11 || idx === 12 || idx === 18 || idx === 22;
              const hasBooking2 = idx === 12;
              return (
                <div key={idx} className="p-2 min-h-[80px] flex flex-col justify-between bg-white dark:bg-transparent">
                  <div className="h-4 w-4 rounded bg-slate-200 dark:bg-slate-800 self-end" />
                  {hasBooking && (
                    <div className="flex-1 flex flex-col justify-end gap-1 mt-2">
                      <div className="h-4 w-full rounded bg-brand-50/50 dark:bg-brand-950/20 border border-brand-100/10" />
                      {hasBooking2 && (
                        <div className="h-4 w-4/5 rounded bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100/10" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="mb-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-slate-100">
            My Classes
          </h1>
          <Button
            onClick={() => setBookModalOpen(true)}
            className="sm:w-auto w-full"
          >
            <Plus className="h-4 w-4" />
            Book a Class
          </Button>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
          >
            {error}
          </div>
        )}

        {!error && bookings.length === 0 && (
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center dark:border-slate-700 dark:bg-slate-800/50">
            <p className="text-sm text-slate-600 dark:text-slate-400 md:text-base">
              No bookings yet. Start by browsing tutors and booking a class.
            </p>
          </div>
        )}
      </div>

      {/* Full-page month calendar */}
      {!error && (
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <Calendar
            yearAndMonth={yearAndMonth}
            onYearAndMonthChange={setYearAndMonth}
            renderDay={(calendarDayObject) => (
              <>
                <CalendarDayHeader calendarDayObject={calendarDayObject} />
                <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                  {(bookingsByDate[calendarDayObject.dateString] ?? []).map((booking) => (
                    <button
                      key={booking.id}
                      type="button"
                      onClick={() => setSelectedBookingId(booking.id)}
                      title={`${bookingLabel(booking)} · ${format(new Date(booking.slotStartUtc), 'p')}`}
                      className={cn(
                        'w-full shrink-0 truncate rounded border px-1.5 py-0.5 text-left text-[11px] font-medium leading-4 transition-colors cursor-pointer',
                        STATUS_COLORS[booking.status] ?? STATUS_COLORS['pending'],
                      )}
                    >
                      <span className="mr-1 tabular-nums opacity-70">
                        {format(new Date(booking.slotStartUtc), 'h:mm a')}
                      </span>
                      {bookingLabel(booking)}
                    </button>
                  ))}
                </div>
              </>
            )}
          />
        </div>
      )}

      {/* Detail Panel */}
      {selectedBooking && (
        <BookingDetailPanel
          booking={selectedBooking}
          tutor={selectedTutor}
          child={selectedBookingChild}
          onClose={() => setSelectedBookingId(null)}
          onUpdate={handleUpdate}
        />
      )}

      {/* Book a Class Modal */}
      {accessToken && (
        <BookClassModal
          open={bookModalOpen}
          onClose={() => setBookModalOpen(false)}
          accessToken={accessToken}
          childOptions={childrenList}
          onBooked={refreshBookings}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Booking Success Modal */}
      {successDetails && (
        <BookingSuccessModal
          open={successModalOpen}
          onClose={() => {
            setSuccessModalOpen(false);
            setSuccessDetails(null);
          }}
          summaryMessage={successDetails.summaryMessage}
          tutorName={successDetails.tutorName}
          childName={successDetails.childName}
        />
      )}
    </div>
  );
}
